import React from 'react';
import { Key, Shield, Globe } from 'lucide-react';
import { SSOProvider, Role } from '../../types';
import { Theme } from '../../ThemeContext';

interface SSOProviderViewModeProps {
  provider: SSOProvider;
  roles: Role[];
  theme: Theme;
}

const SSOProviderViewMode: React.FC<SSOProviderViewModeProps> = ({ provider, roles, theme }) => {
  const getDefaultRoleName = () => {
    if (!provider.default_role) return 'None';
    const role = roles.find(r => r.role_id === provider.default_role);
    return role ? role.name.toUpperCase() : 'Unknown';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Basic Information */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 24,
      }}>
        <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, marginTop: 0 }}>
          Basic Information
        </h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Provider Name
            </label>
            <div style={{ color: theme.text, fontSize: 16, fontWeight: 600 }}>
              {provider.name}
            </div>
          </div>

          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Provider Type
            </label>
            <div>
              <span style={{
                padding: '4px 8px',
                background: provider.provider_type === 'oidc' ? '#e0f2fe' : '#f0e6ff',
                color: provider.provider_type === 'oidc' ? '#0369a1' : '#7c3aed',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}>
                {provider.provider_type}
              </span>
            </div>
          </div>

          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Status
            </label>
            <div>
              {provider.enabled ? (
                <span style={{
                  padding: '4px 8px',
                  background: '#d1fae5',
                  color: '#065f46',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                }}>
                  Enabled
                </span>
              ) : (
                <span style={{
                  padding: '4px 8px',
                  background: '#fee2e2',
                  color: '#991b1b',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                }}>
                  Disabled
                </span>
              )}
            </div>
          </div>

          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Auto-Create Users
            </label>
            <div style={{ color: theme.text, fontSize: 14 }}>
              {provider.auto_create_users ? 'Yes' : 'No'}
            </div>
          </div>

          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Default Role
            </label>
            <div style={{ color: theme.text, fontSize: 14 }}>
              {getDefaultRoleName()}
            </div>
          </div>

          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Created At
            </label>
            <div style={{ color: theme.text, fontSize: 14 }}>
              {new Date(provider.created_at).toLocaleString()}
            </div>
          </div>

          {provider.updated_at && provider.updated_at !== provider.created_at && (
            <div>
              <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                Updated At
              </label>
              <div style={{ color: theme.text, fontSize: 14 }}>
                {new Date(provider.updated_at).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* OIDC Configuration */}
      {provider.provider_type === 'oidc' && provider.oidc_config && (
        <div style={{
          background: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          padding: 24,
        }}>
          <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Globe size={20} />
            OIDC Configuration
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                Client ID
              </label>
              <div style={{ 
                color: theme.text, 
                fontSize: 14,
                fontFamily: 'monospace',
                padding: 8,
                background: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: 4,
              }}>
                {provider.oidc_config.client_id}
              </div>
            </div>

            <div>
              <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                Client Secret
              </label>
              <div style={{ 
                color: theme.text, 
                fontSize: 14,
                fontFamily: 'monospace',
                padding: 8,
                background: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: 4,
              }}>
                ••••••••••••••••
              </div>
            </div>

            <div>
              <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                Discovery URL
              </label>
              <div style={{ 
                color: theme.text, 
                fontSize: 14,
                fontFamily: 'monospace',
                padding: 8,
                background: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: 4,
                wordBreak: 'break-all',
              }}>
                {provider.oidc_config.discovery_url}
              </div>
            </div>

            <div>
              <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                Scopes
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {provider.oidc_config.scopes.map((scope, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '4px 8px',
                      background: theme.background,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                      fontSize: 12,
                      color: theme.text,
                      fontFamily: 'monospace',
                    }}
                  >
                    {scope}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SAML Configuration */}
      {provider.provider_type === 'saml' && provider.saml_config && (
        <div style={{
          background: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          padding: 24,
        }}>
          <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={20} />
            SAML Configuration
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                Entity ID
              </label>
              <div style={{ 
                color: theme.text, 
                fontSize: 14,
                fontFamily: 'monospace',
                padding: 8,
                background: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: 4,
                wordBreak: 'break-all',
              }}>
                {provider.saml_config.entity_id}
              </div>
            </div>

            <div>
              <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                SSO URL
              </label>
              <div style={{ 
                color: theme.text, 
                fontSize: 14,
                fontFamily: 'monospace',
                padding: 8,
                background: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: 4,
                wordBreak: 'break-all',
              }}>
                {provider.saml_config.sso_url}
              </div>
            </div>

            {provider.saml_config.slo_url && (
              <div>
                <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                  SLO URL
                </label>
                <div style={{ 
                  color: theme.text, 
                  fontSize: 14,
                  fontFamily: 'monospace',
                  padding: 8,
                  background: theme.background,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 4,
                  wordBreak: 'break-all',
                }}>
                  {provider.saml_config.slo_url}
                </div>
              </div>
            )}

            <div>
              <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                X.509 Certificate
              </label>
              <div style={{ 
                color: theme.text, 
                fontSize: 12,
                fontFamily: 'monospace',
                padding: 8,
                background: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: 4,
                maxHeight: 200,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}>
                {provider.saml_config.x509_cert}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SSOProviderViewMode;
